import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '@org/ui';
import { BookHeart, Feather, Sparkles, Users } from 'lucide-react';

// PUBLIC page — the QR code printed on the Journal2Empower bookmark points
// here (plataforma.english2empower.com/journal). It renders without login,
// outside the student `_app` shell; keep this URL stable forever.

// -- Feminist color palette (local copy — same convention as journey.tsx / mural.tsx) --
const P = {
  maxRed: '#D41920',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  black: '#1A1018',
  white: '#FFFFFF',
  cream: '#FFF8F5',
  blush: '#FDF0ED',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightText: '#8A6F69',
  lightBorder: '#F0DAD5',
  shadow: 'rgba(180, 80, 80, 0.12)',
};

const oswald = "'Oswald', sans-serif";
const dmSerif = "'DM Serif Text', serif";

// The Journal's own structure: 12 months, each with a theme and a grammar
// focus (focus labels come from i18n; names are the Journal's, in English).
const MONTHS = [
  'Beginnings',
  'The Texture of Your Days',
  'Where You Come From',
  'The People Who Shape You',
  'Stronger Than You Knew',
  'Your Voice, Your Boundaries',
  'Work & Ambition',
  'On Your Own Terms',
  'Women & the World',
  'Growth & Change',
  'Your Future Self',
  'The Year You Built',
];

const PRE_ENROLLMENT_FORM_URL = 'https://english2empower.com/formulario/';

const JOURNAL_STYLES = `
.jr-btn:hover { filter: brightness(1.06); }
.jr-btn:focus-visible, .jr-link:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.jr-month { transition: transform .15s ease, box-shadow .15s ease; }
.jr-month:hover { transform: translateY(-2px); box-shadow: 6px 10px 22px rgba(120, 40, 40, 0.18); }
@media (prefers-reduced-motion: reduce) {
  .jr-month { transition: none; }
  .jr-month:hover { transform: none; }
}
`;

const bodyStyle: CSSProperties = {
  color: P.midText,
  fontSize: 16,
  lineHeight: 1.6,
  margin: 0,
};

const btnRed: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  padding: '14px 24px',
  borderRadius: 4,
  border: `2px solid ${P.maxRed}`,
  background: P.maxRed,
  color: P.white,
  lineHeight: 1.2,
  cursor: 'pointer',
};

const btnGhost: CSSProperties = {
  ...btnRed,
  background: 'transparent',
  borderColor: P.black,
  color: P.black,
};

export const Route = createFileRoute('/journal')({
  component: JournalLandingPage,
});

function JournalLandingPage() {
  const { t, i18n } = useTranslation('p2e');
  const { isLoggedIn } = useAuth();
  const { loginWithRedirect } = useAuth0();

  // Anonymous readers are mostly Brazilian Journal buyers: when no student
  // profile drives the language, follow the browser's Portuguese preference.
  useEffect(() => {
    if (!isLoggedIn && i18n.language === 'en' && navigator.language?.toLowerCase().startsWith('pt')) {
      void i18n.changeLanguage('pt');
    }
  }, [isLoggedIn, i18n]);

  const signup = () =>
    loginWithRedirect({
      authorizationParams: { screen_hint: 'signup' },
      appState: { returnTo: '/journal' },
    });
  const login = () =>
    loginWithRedirect({ appState: { returnTo: '/journal' } });

  const sectionCard: CSSProperties = {
    background: P.white,
    border: `1px solid ${P.lightBorder}`,
    borderRadius: 8,
    boxShadow: `6px 10px 22px ${P.shadow}`,
    padding: 24,
  };

  return (
    <div style={{ minHeight: '100svh', background: P.cream }}>
      <style dangerouslySetInnerHTML={{ __html: JOURNAL_STYLES }} />

      <div style={{ maxWidth: 820, margin: '0 auto', padding: '40px 20px 60px' }}>
        {/* Brand header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 36 }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: 10,
              background: '#cf2e2e',
            }}
          >
            <img src="/icon-hand-small-white.png" alt="" aria-hidden style={{ width: 20, height: 20, objectFit: 'contain' }} />
          </span>
          <span style={{ fontWeight: 600, fontSize: 17, color: P.darkText }}>English2Empower</span>
        </div>

        {/* Hero */}
        <header style={{ marginBottom: 28 }}>
          <p
            style={{
              fontFamily: oswald,
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: P.maxRed,
              margin: 0,
            }}
          >
            {t('journalLanding.eyebrow')}
          </p>
          <h1
            style={{
              fontFamily: oswald,
              fontSize: 38,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: P.darkText,
              margin: '6px 0 14px',
              lineHeight: 1.05,
            }}
          >
            {t('journalLanding.title')}
          </h1>
          <p style={{ ...bodyStyle, maxWidth: '58ch' }}>{t('journalLanding.welcome')}</p>
        </header>

        {/* CTA */}
        <section style={{ ...sectionCard, marginBottom: 30 }}>
          {isLoggedIn ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
              <Sparkles size={20} color={P.maxRed} aria-hidden />
              <p style={{ ...bodyStyle, flex: 1, minWidth: 200 }}>{t('journalLanding.loggedInNote')}</p>
              <Link to="/" className="jr-btn" style={{ ...btnRed, textDecoration: 'none' }}>
                {t('journalLanding.goToPlatform')}
              </Link>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <button type="button" className="jr-btn" style={btnRed} onClick={signup}>
                  {t('journalLanding.ctaSignup')}
                </button>
                <button type="button" className="jr-btn" style={btnGhost} onClick={login}>
                  {t('journalLanding.ctaLogin')}
                </button>
              </div>
              <p style={{ ...bodyStyle, fontSize: 13, color: P.lightText, marginTop: 10 }}>
                {t('journalLanding.ctaNote')}
              </p>
            </div>
          )}
        </section>

        {/* The 12 months */}
        <section style={{ marginBottom: 34 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Feather size={18} color={P.maxRed} aria-hidden />
            <h2
              style={{
                fontFamily: dmSerif,
                fontWeight: 400,
                fontSize: 26,
                color: P.darkText,
                margin: 0,
              }}
            >
              {t('journalLanding.monthsTitle')}
            </h2>
          </div>
          <p style={{ ...bodyStyle, marginBottom: 16 }}>{t('journalLanding.monthsIntro')}</p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 12,
            }}
          >
            {MONTHS.map((name, index) => (
              <div
                key={name}
                className="jr-month"
                style={{
                  background: index % 3 === 1 ? P.baby : index % 3 === 2 ? P.blush : P.white,
                  border: `1px solid ${P.lightBorder}`,
                  borderRadius: 8,
                  boxShadow: `4px 6px 16px ${P.shadow}`,
                  padding: 16,
                }}
              >
                <p
                  style={{
                    fontFamily: oswald,
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: P.maxRed,
                    margin: 0,
                  }}
                >
                  {t('journalLanding.monthLabel', { num: index + 1 })}
                </p>
                <p style={{ fontFamily: dmSerif, fontSize: 19, color: P.darkText, margin: '4px 0 6px', lineHeight: 1.15 }}>
                  {name}
                </p>
                <p style={{ fontSize: 13, color: P.lightText, margin: 0 }}>
                  {t(`journalLanding.focus.m${index + 1}`)}
                </p>
              </div>
            ))}
          </div>
          <p style={{ ...bodyStyle, fontSize: 13, color: P.lightText, marginTop: 14, fontStyle: 'italic' }}>
            {t('journalLanding.comingSoon')}
          </p>
        </section>

        {/* About E2E */}
        <section style={{ ...sectionCard, marginBottom: 30 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <BookHeart size={18} color={P.maxRed} aria-hidden />
            <h2 style={{ fontFamily: dmSerif, fontWeight: 400, fontSize: 24, color: P.darkText, margin: 0 }}>
              {t('journalLanding.aboutTitle')}
            </h2>
          </div>
          <p style={bodyStyle}>{t('journalLanding.aboutBody')}</p>
        </section>

        {/* Courses showcase */}
        <section>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Users size={18} color={P.maxRed} aria-hidden />
            <h2 style={{ fontFamily: dmSerif, fontWeight: 400, fontSize: 24, color: P.darkText, margin: 0 }}>
              {t('journalLanding.coursesTitle')}
            </h2>
          </div>
          <p style={{ ...bodyStyle, marginBottom: 14 }}>{t('journalLanding.coursesIntro')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
            {(['jornada', 'clube', 'leaders'] as const).map((course) => (
              <div key={course} style={{ ...sectionCard, padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ fontFamily: oswald, fontWeight: 600, fontSize: 15, letterSpacing: '0.06em', textTransform: 'uppercase', color: P.darkText, margin: 0 }}>
                  {t(`journalLanding.courses.${course}.name`)}
                </p>
                <p style={{ ...bodyStyle, fontSize: 14, flex: 1 }}>{t(`journalLanding.courses.${course}.desc`)}</p>
                {/* Points at the pre-enrollment form until the in-platform purchase area exists. */}
                <a
                  className="jr-link"
                  href={PRE_ENROLLMENT_FORM_URL}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontFamily: oswald,
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: P.maxRed,
                    textDecoration: 'none',
                  }}
                >
                  {t('journalLanding.courseCta')} →
                </a>
              </div>
            ))}
          </div>
        </section>

        <p style={{ textAlign: 'center', fontSize: 12, color: P.lightText, marginTop: 40 }}>
          English2Empower · plataforma.english2empower.com
        </p>
      </div>
    </div>
  );
}
